import { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// Draggable Image Item Component
const DraggableImageItem = ({ 
  image, 
  index, 
  type, // 'existing' or 'new'
  onRemove, 
  moveImage,
  onSetPrimary,
  isPrimary,
  disabled 
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: type === 'existing' ? 'existing-image' : 'new-image',
    item: { index, type },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ['existing-image', 'new-image'],
    hover: (draggedItem) => {
      if (draggedItem.index !== index && draggedItem.type === type) {
        moveImage(draggedItem.index, index, type);
        draggedItem.index = index;
      }
    },
  });

  return (
    <div 
      ref={(node) => drag(drop(node))}
      className={`card mb-2 ${isDragging ? 'opacity-50' : ''}`}
      style={{ cursor: disabled ? 'not-allowed' : 'move' }}
    >
      <div className="card-body py-2">
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0 me-3 position-relative">
            <img
              src={type === 'existing' ? image.url : image.preview}
              alt={type === 'existing' ? `Image ${index + 1}` : image.name}
              className="rounded"
              style={{ width: "80px", height: "80px", objectFit: "cover" }}
            />
            {isPrimary && (
              <span className="position-absolute top-0 start-0 badge bg-success">
                Primary
              </span>
            )}
          </div>
          
          <div className="flex-grow-1 overflow-hidden">
            {type === 'existing' ? (
              <>
                <h6 className="mb-1">Image #{index + 1}</h6>
                <p className="mb-0 text-muted">ID: {image.id}</p>
              </>
            ) : (
              <>
                <h6 className="mb-1 text-truncate">{image.name}</h6>
                <p className="mb-0 text-muted">{image.formattedSize}</p>
              </>
            )}
          </div>
          
          <div className="d-flex align-items-center gap-2">
            {/* Set Primary Button - Only for existing images */}
            {type === 'existing' && !isPrimary && (
              <button
                type="button"
                className="btn btn-sm btn-outline-warning"
                onClick={() => onSetPrimary(image.id)}
                disabled={disabled}
                title="Set as primary image"
              >
                <i className="bx bx-star"></i>
              </button>
            )}
            
            {/* Primary Indicator */}
            {type === 'existing' && isPrimary && (
              <span className="badge bg-warning text-dark">
                <i className="bx bx-star me-1"></i> Primary
              </span>
            )}
            
            {/* Drag Handle */}
            <i className="bx bx-menu text-muted" style={{ fontSize: '1.5rem' }} />
            
            {/* Remove Button */}
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => onRemove(index, type, image.id)}
              disabled={disabled}
            >
              <i className="bx bx-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main ImageUpload Component
const ImageUpload = ({
  existingImages = [],
  onImagesChange,
  maxFiles = 5,
  maxSizeMB = 5,
  acceptedFileTypes = "image/*",
  multiple = true,
  title = "Upload Images",
  subtitle = "Drag & drop images here or click to browse",
  disabled = false
}) => {
  const [newFiles, setNewFiles] = useState([]);
  const [orderedExistingImages, setOrderedExistingImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [deletedIds, setDeletedIds] = useState([]);

  // Initialize ordered existing images
  useEffect(() => {
    setOrderedExistingImages(existingImages);
  }, [existingImages]);

  // Format file size
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // Move image in the list
  const moveImage = useCallback((fromIndex, toIndex, type) => {
    if (type === 'existing') {
      setOrderedExistingImages(prev => {
        const newImages = [...prev];
        const [movedItem] = newImages.splice(fromIndex, 1);
        newImages.splice(toIndex, 0, movedItem);
        
        // Notify parent of reordering
        onImagesChange?.({
          allImages: getAllImages(newImages, newFiles),
          reorderedImages: newImages,
          deletedIds,
          primaryImageId: getPrimaryImageId(newImages)
        });
        
        return newImages;
      });
    } else {
      setNewFiles(prev => {
        const newFilesList = [...prev];
        const [movedItem] = newFilesList.splice(fromIndex, 1);
        newFilesList.splice(toIndex, 0, movedItem);
        
        // Notify parent of reordering
        onImagesChange?.({
          allImages: getAllImages(orderedExistingImages, newFilesList),
          newFiles: newFilesList,
          deletedIds,
          primaryImageId: getPrimaryImageId(orderedExistingImages)
        });
        
        return newFilesList;
      });
    }
  }, [orderedExistingImages, newFiles, deletedIds, onImagesChange]);

  // Get all images combined
  const getAllImages = (existing, newFilesList) => {
    return [
      ...existing.map(img => ({ ...img, type: 'existing' })),
      ...newFilesList.map(file => ({ ...file, type: 'new' }))
    ];
  };

  // Get primary image ID
  const getPrimaryImageId = (images) => {
    const primary = images.find(img => img.isPrimary);
    return primary?.id;
  };

  // Handle file processing
  const handleAcceptedFiles = useCallback((files) => {
    if (disabled) return;

    // Check if exceeding max files
    if (orderedExistingImages.length + newFiles.length + files.length > maxFiles) {
      alert(`You can only upload up to ${maxFiles} files`);
      return;
    }

    // Check file sizes
    const oversizedFiles = files.filter(file => file.size > maxSizeMB * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      alert(`Some files exceed the maximum size of ${maxSizeMB}MB`);
      return;
    }

    // Process valid files
    const validFiles = files.filter(file => file.size <= maxSizeMB * 1024 * 1024);
    
    const processedFiles = validFiles.map((file) => ({
      file,
      name: file.name,
      preview: URL.createObjectURL(file),
      formattedSize: formatBytes(file.size),
      id: `new-${Date.now()}-${Math.random()}`, // Temporary ID
      isPrimary: false
    }));

    const updatedNewFiles = [...newFiles, ...processedFiles];
    setNewFiles(updatedNewFiles);
    
    // Notify parent component
    onImagesChange?.({
      allImages: getAllImages(orderedExistingImages, updatedNewFiles),
      newFiles: updatedNewFiles,
      deletedIds,
      primaryImageId: getPrimaryImageId(orderedExistingImages)
    });
  }, [orderedExistingImages, newFiles, maxFiles, maxSizeMB, onImagesChange, deletedIds, disabled]);

  // Handle remove
  const removeFile = (index, type, imageId) => {
    if (disabled) return;

    if (type === 'existing') {
      // Mark for deletion
      setDeletedIds(prev => [...prev, imageId]);
      
      // Remove from display
      const updatedExisting = orderedExistingImages.filter((_, i) => i !== index);
      setOrderedExistingImages(updatedExisting);
      
      // Notify parent
      onImagesChange?.({
        allImages: getAllImages(updatedExisting, newFiles),
        reorderedImages: updatedExisting,
        deletedIds: [...deletedIds, imageId],
        primaryImageId: getPrimaryImageId(updatedExisting)
      });
    } else {
      // Clean up preview URL
      URL.revokeObjectURL(newFiles[index].preview);
      
      // Remove new file
      const updatedNewFiles = newFiles.filter((_, i) => i !== index);
      setNewFiles(updatedNewFiles);
      
      // Notify parent
      onImagesChange?.({
        allImages: getAllImages(orderedExistingImages, updatedNewFiles),
        newFiles: updatedNewFiles,
        deletedIds,
        primaryImageId: getPrimaryImageId(orderedExistingImages)
      });
    }
  };

  // Handle set primary image
  const handleSetPrimary = (imageId) => {
    if (disabled) return;

    const updatedExisting = orderedExistingImages.map(img => ({
      ...img,
      isPrimary: img.id === imageId
    }));
    
    setOrderedExistingImages(updatedExisting);
    
    // Notify parent
    onImagesChange?.({
      allImages: getAllImages(updatedExisting, newFiles),
      reorderedImages: updatedExisting,
      deletedIds,
      primaryImageId: imageId
    });
  };

  // Clear all files
  const clearAllFiles = () => {
    if (disabled) return;

    // Clean up preview URLs
    newFiles.forEach(file => URL.revokeObjectURL(file.preview));
    
    // Mark all existing images for deletion
    const allExistingIds = orderedExistingImages.map(img => img.id);
    
    setNewFiles([]);
    setOrderedExistingImages([]);
    setDeletedIds(allExistingIds);
    
    onImagesChange?.({
      allImages: [],
      newFiles: [],
      reorderedImages: [],
      deletedIds: allExistingIds,
      primaryImageId: null
    });
  };

  // Drag & drop handlers
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    handleAcceptedFiles(files);
  };

  // Handle file input
  const handleFileInputChange = (e) => {
    if (disabled) return;
    const files = Array.from(e.target.files);
    handleAcceptedFiles(files);
    e.target.value = ''; // Reset input
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="image-upload">
        <h6 className="card-title">{title}</h6>
        {subtitle && <p className="text-muted mb-3">{subtitle}</p>}
        
        {/* Dropzone */}
        <div 
          className={`dropzone ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
        >
          <div className="dz-message needsclick">
            <div className="mb-3">
              <i className="display-4 text-muted bx bxs-cloud-upload"></i>
            </div>
            <h5>Drop files here or click to upload</h5>
            <span className="text-muted font-size-13">
              (Max size: {maxSizeMB}MB, Max files: {maxFiles})
            </span>
            <input 
              type="file" 
              className="d-none" 
              id="fileInput"
              multiple={multiple}
              accept={acceptedFileTypes}
              onChange={handleFileInputChange}
              disabled={disabled}
            />
            <label 
              htmlFor="fileInput" 
              className={`btn btn-primary mt-3 ${disabled ? 'disabled' : ''}`}
              style={{ pointerEvents: disabled ? 'none' : 'auto' }}
            >
              Browse Files
            </label>
          </div>
        </div>
        
        {/* Image Lists */}
        {(orderedExistingImages.length > 0 || newFiles.length > 0) && (
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                All Images ({orderedExistingImages.length + newFiles.length})
                {deletedIds.length > 0 && (
                  <span className="badge bg-danger ms-2">
                    {deletedIds.length} marked for deletion
                  </span>
                )}
              </h6>
              {!disabled && (
                <button 
                  type="button" 
                  className="btn btn-sm btn-outline-danger"
                  onClick={clearAllFiles}
                >
                  Clear All
                </button>
              )}
            </div>
            
            <div className="selected-files">
              {/* Existing Images */}
              {orderedExistingImages.map((image, index) => (
                <DraggableImageItem
                  key={image.id}
                  image={image}
                  index={index}
                  type="existing"
                  onRemove={removeFile}
                  moveImage={moveImage}
                  onSetPrimary={handleSetPrimary}
                  isPrimary={image.isPrimary}
                  disabled={disabled}
                />
              ))}
              
              {/* New Files */}
              {newFiles.map((file, index) => (
                <DraggableImageItem
                  key={file.id}
                  image={file}
                  index={index}
                  type="new"
                  onRemove={removeFile}
                  moveImage={moveImage}
                  onSetPrimary={() => {}} // New files can't be primary until uploaded
                  isPrimary={false}
                  disabled={disabled}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

ImageUpload.propTypes = {
  existingImages: PropTypes.array,
  onImagesChange: PropTypes.func,
  maxFiles: PropTypes.number,
  maxSizeMB: PropTypes.number,
  acceptedFileTypes: PropTypes.string,
  multiple: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  disabled: PropTypes.bool
};

export default ImageUpload;